import * as React from "react";
import { Box } from "@mui/material";

/** The designs' `.neura-switch` (NeuraDesigns/assets/neura.css:626-640), rebuilt rather than
 *  restyled onto MUI's Switch.
 *
 *  MUI's Switch is a 58×38 box: a 20px thumb inside a 38×20 track, with 12px of padding all
 *  round for the ripple. The design's is 38×22 overall — no padding, no ripple — so matching
 *  it means overriding the width, height, padding, thumb size and translation, then killing
 *  the ripple, at which point nothing of MUI's is left but the state handling. Two of those
 *  overrides already exist half-done in HS/Attendance.js and HS/Devices.js, which is how the
 *  same control ended up two different sizes on two pages.
 *
 *  Structured as the design structures it — a label wrapping a visually-hidden checkbox and
 *  a styled track — so the checkbox stays a real checkbox: keyboard toggling, focus-visible
 *  and the label's own click target all come from the browser rather than being reimplemented.
 *
 *  Tokens resolved from :root in that stylesheet:
 *    accent #FDB940 · ease-out cubic-bezier(0.4, 0, 0.2, 1)
 */

const ACCENT = "#FDB940";
const EASE = "cubic-bezier(0.4, 0, 0.2, 1)";

export default function DesignSwitch({ checked, onChange, disabled, id, "aria-label": ariaLabel }) {
  return (
    <Box
      component="label"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        cursor: disabled ? "default" : "pointer",
        flexShrink: 0,
      }}
    >
      <Box
        component="input"
        type="checkbox"
        id={id}
        aria-label={ariaLabel}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        sx={{
          // Visually hidden, not display:none — a hidden input takes no focus and would
          // strip the control's keyboard behaviour along with its appearance.
          position: "absolute",
          opacity: 0,
          width: "1px",
          height: "1px",
          margin: 0,
          "&:checked + .neura-switch__track": { backgroundColor: ACCENT },
          "&:checked + .neura-switch__track::after": { transform: "translateX(16px)" },
          "&:focus-visible + .neura-switch__track": { outline: `2px solid ${ACCENT}`, outlineOffset: "2px" },
          "&:disabled + .neura-switch__track": { opacity: 0.5 },
        }}
      />
      <Box
        className="neura-switch__track"
        sx={{
          position: "relative",
          width: "38px",
          height: "22px",
          borderRadius: "999px",
          backgroundColor: "rgba(255,255,255,0.08)",
          transition: `background 0.2s ${EASE}`,
          flexShrink: 0,
          "&::after": {
            content: '""',
            position: "absolute",
            top: "2px",
            left: "2px",
            width: "18px",
            height: "18px",
            backgroundColor: "#fff",
            borderRadius: "50%",
            transition: `transform 0.2s ${EASE}`,
          },
        }}
      />
    </Box>
  );
}
